import React, { useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Info, Sparkles, CalendarDays, Eye, Wallet, MapPin, Calendar, RotateCcw, Lightbulb, X } from 'lucide-react';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import type { Itinerary, DayItinerary, AgentEvent, FormData } from '../../types';
import { ConnieAvatar } from '../../components/ConnieAvatar';
import { ActivityMap } from '../../components/ActivityMap';
import { GeneratingScreen } from '../GeneratingPanel/GeneratingScreen';

/** Returns true only for http/https URLs — blocks javascript:, data:, and other dangerous protocols. */
function isSafeUrl(url: string | undefined | null): url is string {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

interface DayAccordionProps {
  day: DayItinerary;
  destination?: string;
}

function DayAccordion({ day, destination }: DayAccordionProps) {
  const [open, setOpen] = useState(false);
  const mobile = useIsMobile();
  return (
    <div style={{
      background: theme.colors.white, borderRadius: theme.borderRadius['3xl'],
      border: `1px solid ${theme.colors.borderLight}`,
      boxShadow: theme.shadows.card, overflow: 'hidden',
      marginBottom: theme.spacing.sm,
      ...(open ? { boxShadow: theme.shadows.card } : {}),
    }}>
      <button type="button" onClick={() => setOpen(o => !o)} style={{
        width: '100%', padding: mobile ? `${theme.spacing.xl} ${theme.spacing.xl}` : `${theme.spacing.xl} ${theme.spacing['2xl']}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: mobile ? theme.spacing.lg : '2.5rem' }}>
          {!mobile && (
          <div style={{ flexShrink: 0 }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%', background: theme.colors.navy, color: theme.colors.white,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: theme.fontWeight.bold, fontSize: theme.fontSize.sm,
              boxShadow: theme.shadows.xl,
            }}>{day.day}</div>
          </div>
          )}
          <div>
            <div style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.3em', color: theme.colors.teal }}>Day {day.day}</div>
            <div style={{ fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize.xl : theme.fontSize['3xl'], fontWeight: theme.fontWeight.bold, letterSpacing: '-0.02em', color: theme.colors.navy }}>{day.theme}</div>
            <div style={{ fontSize: theme.fontSize.sm, color: theme.colors.muted, fontWeight: theme.fontWeight.light, display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
              {day.activities.length} Experiences
              {!open && (
                <div style={{ display: 'flex', marginLeft: theme.spacing.sm }}>
                  {day.activities.slice(0, 3).map((act, i) => (
                    act.place_id && isSafeUrl(act.thumbnail) ? (
                      <div key={i} style={{
                        width: 24, height: 24, borderRadius: '50%', border: '2px solid white',
                        overflow: 'hidden', boxShadow: theme.shadows.card, marginLeft: i > 0 ? -8 : 0,
                      }}>
                        <img src={act.thumbnail} alt="" referrerPolicy="no-referrer" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      </div>
                    ) : null
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xl }}>
          <span style={{
            width: mobile ? 36 : 48, height: mobile ? 36 : 48, borderRadius: '50%',
            border: `1px solid ${theme.colors.borderLight}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: `all ${theme.transitions.normal}`,
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
            background: open ? theme.colors.navy : 'transparent',
            color: open ? theme.colors.white : theme.colors.muted,
            fontSize: theme.fontSize.xs, flexShrink: 0,
          }}>▼</span>
        </div>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
            style={{ overflow: 'hidden' }}
          >
            {/* Day theme banner */}
            <div style={{
              background: theme.colors.navy, padding: mobile ? `${theme.spacing.xl} ${theme.spacing.xl}` : `${theme.spacing['3xl']} ${theme.spacing['4xl']}`,
              position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 120%, rgba(0,242,254,0.15) 0%, transparent 60%)' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <h4 style={{ fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize.xl : theme.fontSize['3xl'], fontWeight: theme.fontWeight.bold, color: theme.colors.white, margin: 0 }}>{day.theme}</h4>
                <p style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontSize: theme.fontSize.lg, color: 'rgba(255,255,255,0.5)', margin: '4px 0 0' }}>A day of discovery and wonder</p>
              </div>
            </div>

            <div style={{ padding: mobile ? `${theme.spacing.xl} ${theme.spacing.xl}` : `${theme.spacing['3xl']} ${theme.spacing['4xl']}`, display: 'flex', flexDirection: 'column', gap: mobile ? '1.5rem' : '3rem' }}>
              {day.activities.map((act, i) => {
                return (
                <React.Fragment key={i}>
                {i > 0 && <div style={{ borderTop: `1px solid ${theme.colors.borderLight}`, margin: `0 ${mobile ? theme.spacing.lg : theme.spacing['2xl']}` }} />}
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.08, duration: 0.5, ease: [0.23, 1, 0.32, 1] }}
                  style={{
                    display: 'flex', flexDirection: mobile ? 'column' : 'row', gap: mobile ? theme.spacing.lg : '3rem',
                    padding: mobile ? theme.spacing.lg : theme.spacing['2xl'], borderRadius: theme.borderRadius['2xl'],
                    transition: 'all 0.5s ease',
                    border: '1px solid transparent',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(0,242,254,0.03)';
                    e.currentTarget.style.borderColor = theme.colors.tealBorder;
                    e.currentTarget.style.boxShadow = theme.shadows.card;
                    const title = e.currentTarget.querySelector('[data-activity-title]') as HTMLElement;
                    if (title) title.style.color = theme.colors.teal;
                    const img = e.currentTarget.querySelector('img[data-activity-thumb]') as HTMLElement;
                    if (img) img.style.transform = 'scale(1.05)';
                    const mapOverlay = e.currentTarget.querySelector('[data-map-overlay]') as HTMLElement;
                    if (mapOverlay) mapOverlay.style.borderColor = theme.colors.tealBorder;
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.boxShadow = 'none';
                    const title = e.currentTarget.querySelector('[data-activity-title]') as HTMLElement;
                    if (title) title.style.color = theme.colors.navy;
                    const img = e.currentTarget.querySelector('img[data-activity-thumb]') as HTMLElement;
                    if (img) img.style.transform = 'scale(1)';
                    const mapOverlay = e.currentTarget.querySelector('[data-map-overlay]') as HTMLElement;
                    if (mapOverlay) mapOverlay.style.borderColor = 'transparent';
                  }}
                >
                  {act.place_id && isSafeUrl(act.thumbnail) && (
                    <div style={{
                      flexShrink: 0, width: mobile ? '100%' : 192, height: mobile ? 160 : 192,
                      borderRadius: theme.borderRadius.xl, overflow: 'hidden',
                      boxShadow: theme.shadows.xl,
                    }}>
                      <img src={act.thumbnail} alt={act.activity} referrerPolicy="no-referrer" data-activity-thumb style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease' }} />
                    </div>
                  )}

                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: theme.spacing.lg }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: theme.spacing.md }}>
                      <div>
                        <span style={{ fontSize: '11px', fontWeight: theme.fontWeight.bold, color: theme.colors.teal, textTransform: 'uppercase', letterSpacing: '0.3em' }}>{act.time}</span>
                        <div data-activity-title style={{
                          fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize.lg : theme.fontSize['2xl'], fontWeight: theme.fontWeight.bold,
                          letterSpacing: '-0.02em', color: theme.colors.navy, transition: 'color 0.5s ease',
                        }}>{act.activity}</div>
                      </div>
                      {act.cost && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center',
                          padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                          fontSize: theme.fontSize.base, color: theme.colors.teal, fontWeight: theme.fontWeight.bold,
                          whiteSpace: 'nowrap', flexShrink: 0,
                        }}>{act.cost}</span>
                      )}
                    </div>

                    <p style={{ fontSize: mobile ? theme.fontSize.base : theme.fontSize.lg, color: theme.colors.textMuted, lineHeight: 1.7, fontWeight: theme.fontWeight.light, maxWidth: '40rem', margin: 0 }}>{act.description}</p>

                    {act.place_id && (
                      <ActivityMap
                        location={act.location || act.activity}
                        destination={destination}
                        placeId={act.place_id}
                      />
                    )}
                  </div>
                </motion.div>
                </React.Fragment>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function TipsSection({ tips }: { tips: string[] }) {
  const mobile = useIsMobile();
  if (!tips.length) return null;
  return (
    <div style={{
      background: theme.colors.navy, color: theme.colors.white,
      borderRadius: theme.borderRadius['3xl'], padding: mobile ? `${theme.spacing.xl} ${theme.spacing.xl}` : '3rem 4rem',
      position: 'relative', overflow: 'hidden',
      boxShadow: theme.shadows.xl,
    }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 384, height: 384, background: `${theme.colors.teal}33`, filter: 'blur(120px)', transform: 'translate(50%, -50%)' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing['2xl'] }}>
          <Sparkles size={28} color={theme.colors.teal} />
          <div>
            <span style={{ fontFamily: theme.fontFamily.display, fontSize: theme.fontSize['2xl'], fontWeight: theme.fontWeight.light }}>
              Connie's <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>Secret</span> Tips
            </span>
            <p style={{ fontSize: theme.fontSize.sm, color: theme.colors.muted, fontWeight: theme.fontWeight.light, marginTop: 2 }}>A few touches to make your journey unforgettable.</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: mobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))', gap: theme.spacing.lg }}>
          {tips.map((tip, i) => (
            <div key={i} style={{
              display: 'flex', gap: theme.spacing.xl, padding: theme.spacing.xl,
              borderRadius: theme.borderRadius.lg, background: theme.colors.whiteAlpha10,
              border: `1px solid ${theme.colors.whiteAlpha10}`,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: `${theme.colors.teal}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Info size={16} color={theme.colors.teal} />
              </div>
              <p style={{ color: theme.colors.gray300, fontWeight: theme.fontWeight.light, lineHeight: 1.6, fontSize: theme.fontSize.base }}>{tip}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const MAX_BUDGET = 20000;

function parseCurrency(str: string): number {
  return Number(str.replace(/[^0-9.]/g, '')) || 0;
}

function formatCurrency(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function CostSummary({ trip }: { trip: NonNullable<Itinerary['itinerary']> }) {
  const mobile = useIsMobile();
  const breakdown = trip.costBreakdown;
  if (!breakdown || breakdown.length === 0) return null;

  const totalItem = breakdown.find(item => item.category.toUpperCase() === 'TOTAL');
  const lineItems = breakdown.filter(item => item.category.toUpperCase() !== 'TOTAL');
  const totalCost = totalItem ? parseCurrency(totalItem.amount) : 0;
  const difference = MAX_BUDGET - totalCost;
  const isUnder = difference > 0;
  const isOver = difference < 0;

  return (
    <div style={{ marginBottom: theme.spacing['3xl'] }}>
      {/* Breakdown table */}
      {lineItems.length > 0 && (
        <div style={{
          background: theme.colors.white, borderRadius: `${theme.borderRadius['3xl']} ${theme.borderRadius['3xl']} 0 0`,
          border: `1px solid ${theme.colors.borderLight}`, borderBottom: 'none',
          boxShadow: theme.shadows.card, padding: theme.spacing['2xl'],
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginBottom: theme.spacing.xl }}>
            <div style={{
              width: 48, height: 48, borderRadius: theme.borderRadius.lg,
              background: theme.colors.tealBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: theme.colors.teal,
            }}>
              <Wallet size={24} />
            </div>
            <div>
              <div style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.2em', color: theme.colors.teal }}>Estimated Budget</div>
              <div style={{ fontFamily: theme.fontFamily.display, fontSize: theme.fontSize.lg, fontWeight: theme.fontWeight.bold, color: theme.colors.navy }}>Trip Cost Overview</div>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {lineItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: `${theme.spacing.lg} 0`, borderBottom: `1px solid ${theme.colors.borderLight}` }}>
                <span style={{ fontSize: theme.fontSize.base, color: theme.colors.textMuted }}>{item.category}</span>
                <span style={{ fontWeight: theme.fontWeight.bold, color: theme.colors.navy, fontSize: theme.fontSize.base }}>{item.amount}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Total bar */}
      {totalItem && (
        <div style={{
          background: theme.colors.navy,
          borderRadius: lineItems.length > 0 ? `0 0 ${theme.borderRadius['3xl']} ${theme.borderRadius['3xl']}` : theme.borderRadius['3xl'],
          padding: mobile ? `${theme.spacing.xl} ${theme.spacing.xl}` : `${theme.spacing['2xl']} ${theme.spacing['3xl']}`,
          display: 'flex', flexDirection: 'column', gap: theme.spacing.lg,
          position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', top: 0, right: 0, width: 256, height: 256, background: `${theme.colors.teal}15`, filter: 'blur(80px)', transform: 'translate(30%, -50%)' }} />
          <div style={{ display: 'flex', alignItems: mobile ? 'flex-start' : 'center', justifyContent: 'space-between', flexDirection: mobile ? 'column' : 'row', gap: mobile ? theme.spacing.lg : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xl, position: 'relative', zIndex: 1 }}>
              <div style={{
                width: 48, height: 48, borderRadius: theme.borderRadius.lg,
                background: theme.colors.whiteAlpha10, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Wallet size={24} color={theme.colors.whiteAlpha60} />
              </div>
              <div>
                <div style={{ fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.2em', color: theme.colors.teal }}>Estimated Total Cost</div>
                <div style={{ fontSize: theme.fontSize.sm, color: theme.colors.whiteAlpha50, fontWeight: theme.fontWeight.light }}>Flights, accommodation, and activities</div>
              </div>
            </div>
            <div style={{
              fontFamily: theme.fontFamily.display, fontSize: mobile ? '1.8rem' : '2.5rem', fontWeight: theme.fontWeight.bold,
              color: theme.colors.white, position: 'relative', zIndex: 1, letterSpacing: '-0.02em',
            }}>{totalItem.amount}</div>
          </div>
          {(isUnder || isOver) && (
            <div style={{
              borderTop: `1px solid ${theme.colors.whiteAlpha10}`,
              paddingTop: theme.spacing.lg,
              position: 'relative', zIndex: 1,
            }}>
              <span style={{ fontSize: theme.fontSize.lg, color: theme.colors.white }}>
                {isUnder
                  ? <>You're <strong style={{ color: theme.colors.teal }}>{formatCurrency(difference)}</strong> under the {formatCurrency(MAX_BUDGET)} budget — extra for upgrades or future trips!</>
                  : <>This trip exceeds the {formatCurrency(MAX_BUDGET)} budget by <strong style={{ color: theme.colors.error }}>{formatCurrency(Math.abs(difference))}</strong>. Consider adjusting activities or accommodation.</>}
              </span>
            </div>
          )}
        </div>
      )}

    </div>
  );
}

interface ItineraryDisplayProps {
  itinerary: Itinerary;
  agentEvents?: AgentEvent[];
  formData?: FormData | null;
  onModifyTrip?: () => void;
}

export function ItineraryDisplay({ itinerary, agentEvents, formData, onModifyTrip }: ItineraryDisplayProps) {
  const trip = itinerary.itinerary;
  const [showReasoning, setShowReasoning] = useState(false);
  const [showTips, setShowTips] = useState(false);
  const mobile = useIsMobile();
  if (!trip) return null;

  return (
    <div style={{ padding: mobile ? theme.spacing.md : theme.spacing.md }}>
      {/* Hero header — navy background, no photo */}
      <div style={{
        borderRadius: theme.borderRadius['3xl'], overflow: 'hidden',
        boxShadow: theme.shadows.xl, marginBottom: theme.spacing['4xl'],
      }}>
        <div style={{
          background: theme.colors.navy, position: 'relative', overflow: 'hidden',
          padding: mobile ? '1.5rem 1.5rem 1.5rem' : '2.5rem 2rem 2rem', display: 'flex', flexDirection: 'column', alignItems: 'center',
          textAlign: 'center',
        }}>
          <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 50% 120%, rgba(0,242,254,0.12) 0%, transparent 60%)' }} />
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.lg }}>
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.3 }}>
              <ConnieAvatar size={mobile ? 64 : 96} style={{ borderRadius: '50%', border: `4px solid ${theme.colors.white}`, boxShadow: theme.shadows.xl, background: theme.colors.white }} />
            </motion.div>
            <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: theme.spacing.sm,
                padding: `${theme.spacing.xs} ${theme.spacing.lg}`, borderRadius: theme.borderRadius.full,
                background: theme.colors.whiteAlpha10, border: '1px solid rgba(255,255,255,0.2)',
                color: theme.colors.white, fontSize: '10px', fontWeight: theme.fontWeight.bold,
                letterSpacing: '0.3em', textTransform: 'uppercase',
              }}
            >Curated{formData?.userName && formData.userName !== 'Guest' ? ` for ${formData.userName}` : ' for You'}</motion.div>
            <motion.h1 initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.6 }}
              style={{
                fontFamily: theme.fontFamily.display, fontSize: mobile ? '1.8rem' : '3.5rem', fontWeight: theme.fontWeight.light,
                letterSpacing: '-0.04em', lineHeight: 0.95, color: theme.colors.white, margin: 0, maxWidth: '48rem',
              }}
            >{trip.title}</motion.h1>
          </div>
          {/* Destination + date pills */}
          <div style={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', gap: theme.spacing.md, marginTop: theme.spacing.xl, flexWrap: 'wrap', justifyContent: 'center' }}>
            {formData?.destination && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`, borderRadius: theme.borderRadius.full,
                background: theme.colors.whiteAlpha10, border: '1px solid rgba(255,255,255,0.2)',
                color: theme.colors.white, fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.3em',
              }}>
                <MapPin size={12} color={theme.colors.teal} /> {formData.destination}
              </div>
            )}
            {formData?.startDate && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                padding: `${theme.spacing.sm} ${theme.spacing.lg}`, borderRadius: theme.borderRadius.full,
                background: theme.colors.whiteAlpha10, border: '1px solid rgba(255,255,255,0.2)',
                color: theme.colors.white, fontSize: '10px', fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.3em',
              }}>
                <Calendar size={12} color={theme.colors.teal} /> {formData.startDate}
              </div>
            )}
          </div>
          {/* Marketing banner */}
          <p style={{ position: 'relative', zIndex: 1, color: theme.colors.whiteAlpha60, fontSize: mobile ? theme.form.mobile.subtitleSize : theme.form.subtitleSize, fontWeight: theme.fontWeight.light, margin: `${theme.spacing.xl} 0 0`, textAlign: 'center' }}>
            This dream was built on <strong style={{ color: theme.colors.teal }}>production-tested blueprints</strong>, designed to scale from a single traveler to millions of users.
          </p>
        </div>
      </div>

      {/* Summary + actions */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: theme.spacing['2xl'], marginBottom: '3rem' }}>
        <p style={{
          fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontSize: mobile ? theme.fontSize.md : theme.fontSize['2xl'],
          color: theme.colors.textMuted, maxWidth: '70%', lineHeight: 1.6, margin: 0,
        }}>"{trip.summary}"</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.xl, paddingTop: theme.spacing.lg, flexWrap: 'wrap', justifyContent: 'center' }}>
          {onModifyTrip && (
            <button className="btn-secondary" onClick={onModifyTrip}>
              <RotateCcw size={16} /> Modify Trip
            </button>
          )}
          {(agentEvents && agentEvents.length > 0) && (
            <button className="btn-primary" onClick={() => setShowReasoning(true)} style={{
              padding: `${theme.spacing.md} ${theme.spacing['2xl']}`,
              fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.15em',
            }}>
              <Eye size={16} /> View Reasoning
            </button>
          )}
          {trip.tips?.length > 0 && (
            <button className="btn-secondary" onClick={() => setShowTips(true)}>
              <Lightbulb size={16} /> View Tips
            </button>
          )}
        </div>
      </div>

      {showReasoning && agentEvents && (
        <GeneratingScreen
          agentEvents={agentEvents}
          destination={formData?.destination || ''}
          isModal={true}
          onClose={() => setShowReasoning(false)}
        />
      )}

      {/* Day-by-day itinerary */}
      <div style={{ marginBottom: theme.spacing['3xl'] }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: theme.spacing.xl }}>
            <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize['2xl'] : theme.fontSize['4xl'], fontWeight: theme.fontWeight.light, color: theme.colors.navy, margin: 0 }}>
              The <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>Itinerary</span>
            </h2>
            <span style={{ fontSize: theme.fontSize.base, color: theme.colors.muted, fontWeight: theme.fontWeight.light }}>Day-by-day experiences</span>
        </div>

        {trip.days.map(day => (
          <DayAccordion
            key={day.day}
            day={day}
            destination={formData?.destination}
          />
        ))}
      </div>


      {/* Cost summary */}
      <CostSummary trip={trip} />

      {/* Tips modal */}
      <AnimatePresence>
        {showTips && trip.tips?.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowTips(false)}
            style={{
              position: 'fixed', inset: 0, zIndex: 100,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: mobile ? '1rem' : '2rem', background: 'rgba(30,41,59,0.4)',
              backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
            }}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={e => e.stopPropagation()}
              style={{ width: '100%', maxWidth: '64rem', maxHeight: '90vh', overflow: 'auto', borderRadius: theme.borderRadius['3xl'], position: 'relative' }}
            >
              <button
                onClick={() => setShowTips(false)}
                style={{
                  position: 'absolute', top: '2rem', right: '2rem', zIndex: 10,
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: theme.spacing.sm, borderRadius: '50%',
                }}
              >
                <X size={24} color="white" />
              </button>
              <TipsSection tips={trip.tips} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
